// AnomaliesPOC.js — Documentation Fonctionnelle : POC « Anomalies » (module Environnement).
// Aperçu interactif de l'écran de gestion des Anomalies : en-tête + cartes KPI + barre de filtres
// (recherche / fonction / statut / sévérité / type) + bascule tableau/cartes + détail en tiroir latéral
// (caractéristiques, cause racine, résolution, personnes) avec changement de statut & assignation.
// Doublé d'un guide utilisateur pas-à-pas + référence (types / statuts / sévérités / impacts).
// Source de vérité : automata-studio-frontend [AUT][05]-Anomalies.js + automata_environment AnomalyController.
// Les actions sont SIMULÉES localement (démo, aucun appel réseau).
import React, { useState } from 'react';
import {
  Plus, RefreshCw, List as ListIcon, Grid as GridIcon, AlertCircle, AlertTriangle,
  AlertOctagon, Info, Code, ShieldAlert, BarChart2, FileText, UserCheck, PenTool,
  CheckSquare, CheckCircle, XCircle, Slash, Tag, Clock, User, Calendar, Edit2, Trash2,
  X, ArrowRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Tabs, Icon, SearchBar, EmptyState } from '../ui';

// ---- Référentiels (alignés sur l'écran réel + AnomalyController) ------------
// 5 types d'anomalie (AnomalyType).
const ANOMALY_TYPES = [
  { value: 'FUNCTIONAL', label: 'Fonctionnelle', icon: 'Code', color: '#3B82F6' },
  { value: 'TECHNICAL', label: 'Technique', icon: 'ShieldAlert', color: '#8B5CF6' },
  { value: 'PERFORMANCE', label: 'Performance', icon: 'BarChart2', color: '#F59E0B' },
  { value: 'SECURITY', label: 'Sécurité', icon: 'ShieldAlert', color: '#EF4444' },
  { value: 'USER_INTERFACE', label: 'Interface utilisateur', icon: 'FileText', color: '#EC4899' },
];
// 6 statuts (AnomalyStatus) — flux de vie de l'anomalie.
const ANOMALY_STATUSES = [
  { value: 'NEW', label: 'Nouvelle', icon: 'AlertCircle', color: '#3B82F6' },
  { value: 'ASSIGNED', label: 'Assignée', icon: 'UserCheck', color: '#8B5CF6' },
  { value: 'IN_PROGRESS', label: 'En cours', icon: 'PenTool', color: '#F59E0B' },
  { value: 'RESOLVED', label: 'Résolue', icon: 'CheckSquare', color: '#10B981' },
  { value: 'CLOSED', label: 'Fermée', icon: 'CheckCircle', color: '#6B7280' },
  { value: 'REJECTED', label: 'Rejetée', icon: 'Slash', color: '#EF4444' },
];
// 4 sévérités.
const ANOMALY_SEVERITIES = [
  { value: 'CRITICAL', label: 'Critique', icon: 'AlertOctagon', color: '#DC2626' },
  { value: 'HIGH', label: 'Élevée', icon: 'AlertTriangle', color: '#F97316' },
  { value: 'MEDIUM', label: 'Moyenne', icon: 'AlertCircle', color: '#F59E0B' },
  { value: 'LOW', label: 'Faible', icon: 'Info', color: '#3B82F6' },
];
// 4 impacts métier.
const ANOMALY_IMPACTS = [
  { value: 'BLOCKING', label: 'Bloquant', color: '#EF4444' },
  { value: 'MAJOR', label: 'Majeur', color: '#F59E0B' },
  { value: 'MINOR', label: 'Mineur', color: '#3B82F6' },
  { value: 'COSMETIC', label: 'Cosmétique', color: '#6B7280' },
];

const typeOf = (v) => ANOMALY_TYPES.find((t) => t.value === v) || { label: v, icon: 'HelpCircle', color: '#6B7280' };
const statusOf = (v) => ANOMALY_STATUSES.find((s) => s.value === v) || { label: v, icon: 'HelpCircle', color: '#6B7280' };
const severityOf = (v) => ANOMALY_SEVERITIES.find((s) => s.value === v) || { label: v, icon: 'HelpCircle', color: '#6B7280' };
const impactOf = (v) => ANOMALY_IMPACTS.find((i) => i.value === v) || { label: v, color: '#6B7280' };

// Données en dur (démo).
const SAMPLE = [
  { id: 1, title: 'Le bouton « Valider » reste désactivé après saisie', type: 'USER_INTERFACE', severity: 'MEDIUM', impact: 'MINOR', status: 'NEW', detectedDate: '2026-06-13', detectedBy: 's.youssef', assignedTo: null, resolvedBy: null, fonction: 'Saisie virement', description: "Sur le formulaire de virement, le bouton de validation ne s'active pas alors que tous les champs requis sont remplis.", rootCause: '', resolution: '' },
  { id: 2, title: 'Solde incorrect après annulation d\'une opération', type: 'FUNCTIONAL', severity: 'CRITICAL', impact: 'BLOCKING', status: 'IN_PROGRESS', detectedDate: '2026-06-12', detectedBy: 'a.fall', assignedTo: 'k.diallo', resolvedBy: null, fonction: 'Annulation opération', description: "Le solde affiché n'est pas recalculé immédiatement après l'annulation, ce qui induit l'utilisateur en erreur.", rootCause: 'Cache local non invalidé après l\'événement d\'annulation.', resolution: '' },
  { id: 3, title: 'Temps de réponse élevé sur la liste des comptes', type: 'PERFORMANCE', severity: 'HIGH', impact: 'MAJOR', status: 'ASSIGNED', detectedDate: '2026-06-11', detectedBy: 's.youssef', assignedTo: 'm.nguema', resolvedBy: null, fonction: 'Consultation comptes', description: "Le chargement de la liste dépasse 5 s au-delà de 2000 comptes (pas de pagination côté serveur).", rootCause: 'Absence de pagination côté serveur.', resolution: '' },
  { id: 4, title: 'Jeton de session non invalidé à la déconnexion', type: 'SECURITY', severity: 'CRITICAL', impact: 'BLOCKING', status: 'RESOLVED', detectedDate: '2026-06-08', detectedBy: 'k.diallo', assignedTo: 'k.diallo', resolvedBy: 'k.diallo', fonction: 'Authentification', description: "Le jeton JWT reste valide après déconnexion ; corrigé par mise en liste de révocation côté gateway.", rootCause: 'Jeton non ajouté à la liste de révocation.', resolution: 'Liste de révocation (blacklist) ajoutée côté gateway.' },
  { id: 5, title: 'Exception NullPointer lors de l\'export PDF', type: 'TECHNICAL', severity: 'MEDIUM', impact: 'MINOR', status: 'CLOSED', detectedDate: '2026-06-05', detectedBy: 'a.fall', assignedTo: 'a.fall', resolvedBy: 'a.fall', fonction: 'Export documents', description: "L'export échoue quand le libellé de l'agence est vide. Corrigé et vérifié en recette.", rootCause: 'Libellé d\'agence non contrôlé avant rendu.', resolution: 'Contrôle de nullité ajouté ; valeur par défaut appliquée.' },
];

const FONCTIONS = ['Saisie virement', 'Annulation opération', 'Consultation comptes', 'Authentification', 'Export documents'];

// ---- Petits sous-composants -------------------------------------------------
const TypeBadge = ({ type }) => { const t = typeOf(type); return <Badge color={t.color}><Icon name={t.icon} className="h-3 w-3" />{t.label}</Badge>; };
const StatusBadge = ({ status }) => { const s = statusOf(status); return <Badge color={s.color}><Icon name={s.icon} className="h-3 w-3" />{s.label}</Badge>; };
const SeverityBadge = ({ severity }) => { const s = severityOf(severity); return <Badge color={s.color}><Icon name={s.icon} className="h-3 w-3" />{s.label}</Badge>; };
const ImpactBadge = ({ impact }) => { const i = impactOf(impact); return <Badge color={i.color}>{i.label}</Badge>; };

const KpiCard = ({ label, value, icon, color }) => (
  <div className="rounded-2xl p-4 text-white shadow-lg" style={{ background: `linear-gradient(135deg, ${color[0]}, ${color[1]})` }}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-2xl font-bold leading-none">{value}</p>
        <p className="text-white/80 text-xs mt-1">{label}</p>
      </div>
      <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">{icon}</div>
    </div>
  </div>
);

const InfoRow = ({ icon: IconCmp, label, value }) => (
  <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1"><IconCmp className="h-4 w-4" />{label}</div>
    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 break-words">{value || 'N/A'}</div>
  </div>
);

const GuideStep = ({ n, children }) => (
  <li className="flex items-start gap-3">
    <span className="shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">{n}</span>
    <span className="text-sm text-gray-700 dark:text-gray-300">{children}</span>
  </li>
);

// ---- Page -------------------------------------------------------------------
const AnomaliesPOC = () => {
  const [anomalies, setAnomalies] = useState(SAMPLE);
  const [drawerId, setDrawerId] = useState(null); // anomalie ouverte dans le tiroir détail
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [fonctionFilter, setFonctionFilter] = useState('all');
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'grid'
  const [toast, setToast] = useState(''); // message d'action simulée

  const selected = anomalies.find((a) => a.id === drawerId) || null;
  const filtered = anomalies.filter((a) =>
    (statusFilter === 'all' || a.status === statusFilter) &&
    (severityFilter === 'all' || a.severity === severityFilter) &&
    (typeFilter === 'all' || a.type === typeFilter) &&
    (fonctionFilter === 'all' || a.fonction === fonctionFilter) &&
    (`${a.title} ${a.fonction} ${a.description}`.toLowerCase().includes(search.toLowerCase()))
  );

  // KPI calculés (NI-01 de l'écran réel).
  const stats = {
    total: anomalies.length,
    critical: anomalies.filter((a) => a.severity === 'CRITICAL').length,
    open: anomalies.filter((a) => ['NEW', 'ASSIGNED', 'IN_PROGRESS'].includes(a.status)).length,
    resolved: anomalies.filter((a) => ['RESOLVED', 'CLOSED'].includes(a.status)).length,
  };

  const flash = (msg) => { setToast(msg); window.setTimeout(() => setToast(''), 2200); };

  // Actions SIMULÉES (aucun réseau)
  const createAnomaly = () => {
    const id = Math.max(0, ...anomalies.map((a) => a.id)) + 1;
    const created = { id, title: `Nouvelle anomalie #${id}`, type: 'FUNCTIONAL', severity: 'MEDIUM', impact: 'MINOR', status: 'NEW', detectedDate: '2026-06-15', detectedBy: 's.youssef', assignedTo: null, resolvedBy: null, fonction: 'Module Environnement', description: "Anomalie de démonstration créée localement (aucun appel réseau).", rootCause: '', resolution: '' };
    setAnomalies((prev) => [created, ...prev]);
    setDrawerId(id);
    flash('Anomalie créée (simulation)');
  };
  const deleteAnomaly = (id) => {
    setAnomalies((prev) => prev.filter((a) => a.id !== id));
    if (drawerId === id) setDrawerId(null);
    flash('Anomalie supprimée (simulation)');
  };
  const assignAnomaly = () => {
    if (!selected) return;
    setAnomalies((prev) => prev.map((a) => a.id === drawerId
      ? { ...a, assignedTo: 'k.diallo', status: a.status === 'NEW' ? 'ASSIGNED' : a.status }
      : a));
    flash('Anomalie assignée à k.diallo (simulation)');
  };
  const changeStatus = (value) => {
    if (!selected) return;
    setAnomalies((prev) => prev.map((a) => a.id === drawerId
      ? { ...a, status: value, resolvedBy: value === 'RESOLVED' ? (a.assignedTo || 's.youssef') : a.resolvedBy }
      : a));
    flash(`Statut → ${statusOf(value).label} (simulation)`);
  };
  const resolveAnomaly = () => changeStatus('RESOLVED');

  const refresh = () => flash('Liste actualisée (simulation)');
  const refreshFilters = () => { setSearch(''); setStatusFilter('all'); setSeverityFilter('all'); setTypeFilter('all'); setFonctionFilter('all'); };

  const kpiCards = [
    { label: 'Total', value: stats.total, icon: <AlertCircle className="h-6 w-6" />, color: ['#3B82F6', '#4338CA'] },
    { label: 'Critiques', value: stats.critical, icon: <AlertOctagon className="h-6 w-6" />, color: ['#EF4444', '#BE123C'] },
    { label: 'Ouvertes', value: stats.open, icon: <AlertTriangle className="h-6 w-6" />, color: ['#F59E0B', '#C2410C'] },
    { label: 'Résolues', value: stats.resolved, icon: <CheckCircle className="h-6 w-6" />, color: ['#10B981', '#15803D'] },
  ];

  return (
    <div className="space-y-8">
      {/* ===== (1) Intro ===== */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Anomalies</h1>
          <Badge color="#3B82F6">Module Environnement</Badge>
          <Badge color="#F59E0B">POC interactif</Badge>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-300">Aperçu interactif de l'écran <strong>Anomalies</strong> (démo, actions simulées localement).</p>
      </div>

      {/* ===== (2) Réplique interactive de l'IHM ===== */}
      <Card className="overflow-hidden">
        {/* En-tête type app (dégradé bleu/indigo) */}
        <div className="px-5 py-4 bg-gradient-to-r from-blue-500 to-indigo-700 text-white flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center"><AlertCircle className="h-6 w-6" /></div>
            <div>
              <h2 className="text-lg font-bold leading-tight">Gestion des Anomalies</h2>
              <p className="text-xs text-white/80">Suivi et résolution des anomalies détectées — {filtered.length}/{anomalies.length} affichée(s)</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex bg-white/10 rounded-xl p-1">
              {[{ key: 'table', icon: ListIcon }, { key: 'grid', icon: GridIcon }].map((v) => (
                <button key={v.key} onClick={() => setViewMode(v.key)}
                  className={`p-2 rounded-lg transition-colors ${viewMode === v.key ? 'bg-white/20 text-white' : 'text-white/60 hover:text-white'}`}>
                  <v.icon className="h-4 w-4" />
                </button>
              ))}
            </div>
            <button onClick={refresh} className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white"><RefreshCw className="h-4 w-4" /></button>
            <Button onClick={createAnomaly} className="bg-white/20 hover:bg-white/30 text-white"><Plus className="h-4 w-4" />Nouvelle Anomalie</Button>
          </div>
        </div>

        {/* Cartes KPI */}
        <div className="px-5 py-4 grid grid-cols-2 lg:grid-cols-4 gap-3 border-b border-gray-200 dark:border-gray-700">
          {kpiCards.map((k) => <KpiCard key={k.label} {...k} />)}
        </div>

        {/* Barre de filtres : recherche / fonction / statut / sévérité / type */}
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px] max-w-sm"><SearchBar value={search} onChange={setSearch} placeholder="Rechercher une anomalie..." /></div>
          <select value={fonctionFilter} onChange={(e) => setFonctionFilter(e.target.value)} className="px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none">
            <option value="all">Toutes les fonctions</option>
            {FONCTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none">
            <option value="all">Tous les statuts</option>
            {ANOMALY_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)} className="px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none">
            <option value="all">Toutes les sévérités</option>
            {ANOMALY_SEVERITIES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none">
            <option value="all">Tous les types</option>
            {ANOMALY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <Button variant="ghost" size="sm" onClick={refreshFilters}><RefreshCw className="h-4 w-4" />Réinitialiser</Button>
        </div>

        {/* Toast d'action simulée */}
        {toast && (
          <div className="px-5 py-2 bg-emerald-50 dark:bg-emerald-500/10 border-b border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-sm flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />{toast}
          </div>
        )}

        {/* Contenu : tableau OU cartes */}
        <div className="p-5">
          {filtered.length === 0 ? (
            <EmptyState icon={AlertCircle} title="Aucune anomalie trouvée" message="Aucune anomalie ne correspond aux filtres sélectionnés." />
          ) : viewMode === 'table' ? (
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                  <tr>
                    {['#', 'Titre', 'Type', 'Sévérité', 'Impact', 'Fonction', 'Détection', 'Assigné', 'Statut', ''].map((h, i) => (
                      <th key={i} className="text-left font-semibold px-4 py-2 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {filtered.map((a) => (
                    <tr key={a.id} onClick={() => setDrawerId(a.id)} className="cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/40">
                      <td className="px-4 py-3 text-gray-400">{a.id}</td>
                      <td className="px-4 py-3 max-w-[280px]">
                        <div className="font-medium text-gray-900 dark:text-white truncate">{a.title}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{a.description}</div>
                      </td>
                      <td className="px-4 py-3"><TypeBadge type={a.type} /></td>
                      <td className="px-4 py-3"><SeverityBadge severity={a.severity} /></td>
                      <td className="px-4 py-3"><ImpactBadge impact={a.impact} /></td>
                      <td className="px-4 py-3"><Badge color="#14B8A6">{a.fonction}</Badge></td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">{a.detectedDate}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{a.assignedTo
                        ? <Badge><User className="h-3 w-3" />{a.assignedTo}</Badge>
                        : <span className="text-xs text-gray-400">Non assignée</span>}</td>
                      <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={(e) => { e.stopPropagation(); setDrawerId(a.id); }} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400"><Edit2 className="h-4 w-4" /></button>
                          <button onClick={(e) => { e.stopPropagation(); deleteAnomaly(a.id); }} className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 text-red-400"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((a) => (
                <div key={a.id} onClick={() => setDrawerId(a.id)} className="relative flex flex-col rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:shadow-md transition-shadow cursor-pointer">
                  <div className="absolute top-3 right-3"><SeverityBadge severity={a.severity} /></div>
                  <div className="p-4 flex-1">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 pr-24 truncate">{a.title}</h3>
                    <div className="flex flex-wrap gap-1.5 mb-3"><StatusBadge status={a.status} /><TypeBadge type={a.type} /><ImpactBadge impact={a.impact} /></div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 line-clamp-2">{a.description}</p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><p className="text-gray-400 dark:text-gray-500">Détection</p><p className="text-gray-700 dark:text-gray-200">{a.detectedDate}</p></div>
                      <div><p className="text-gray-400 dark:text-gray-500">Fonction</p><p className="text-gray-700 dark:text-gray-200 truncate">{a.fonction}</p></div>
                      <div className="col-span-2"><p className="text-gray-400 dark:text-gray-500">Assignée à</p><p className="text-gray-700 dark:text-gray-200">{a.assignedTo || 'Non assignée'}</p></div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 dark:border-gray-700/50">
                    <div className="flex gap-1">
                      <button onClick={(e) => { e.stopPropagation(); setDrawerId(a.id); }} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400"><Edit2 className="h-4 w-4" /></button>
                      <button onClick={(e) => { e.stopPropagation(); deleteAnomaly(a.id); }} className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 text-red-400"><Trash2 className="h-4 w-4" /></button>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); setDrawerId(a.id); }} className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1">Détails <Info className="h-3 w-3" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* ===== Tiroir détail (simulé en bloc sous l'IHM) ===== */}
      {selected && (
        <Card className="overflow-hidden border-l-4 border-l-blue-500">
          <div className="px-5 py-4 bg-gradient-to-r from-blue-500 to-indigo-700 text-white flex items-center justify-between">
            <h2 className="text-lg font-semibold flex items-center gap-2"><FileText className="h-5 w-5" />Détail de l'anomalie</h2>
            <button onClick={() => setDrawerId(null)} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white"><X className="h-4 w-4" /></button>
          </div>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex flex-wrap gap-1.5 mb-2"><SeverityBadge severity={selected.severity} /><StatusBadge status={selected.status} /><TypeBadge type={selected.type} /><ImpactBadge impact={selected.impact} /></div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">{selected.title}</h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{selected.description}</p>
              </div>

              {/* Caractéristiques + personnes */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <InfoRow icon={Code} label="Fonction associée" value={selected.fonction} />
                <InfoRow icon={Calendar} label="Date de détection" value={selected.detectedDate} />
                <InfoRow icon={User} label="Détectée par" value={selected.detectedBy} />
                <InfoRow icon={Clock} label="ID interne" value={`#${selected.id}`} />
                <InfoRow icon={UserCheck} label="Assignée à" value={selected.assignedTo || 'Non assignée'} />
                <InfoRow icon={CheckSquare} label="Résolue par" value={selected.resolvedBy} />
              </div>

              {/* Cause racine + résolution */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Cause racine</p>
                  <p className="text-sm text-gray-700 dark:text-gray-300">{selected.rootCause || 'Non spécifiée'}</p>
                </div>
                <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Résolution</p>
                  <p className="text-sm text-gray-700 dark:text-gray-300">{selected.resolution || 'Non spécifiée'}</p>
                </div>
              </div>

              {/* Changement de statut simulé */}
              <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white mb-1"><Tag className="h-4 w-4 text-blue-500" />Changer le statut</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">Sélectionnez un nouveau statut (simulation locale).</div>
                <select value={selected.status} onChange={(e) => changeStatus(e.target.value)} className="w-full sm:max-w-xs px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none">
                  {ANOMALY_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                {selected.status === 'RESOLVED' && (
                  <div className="mt-3 flex items-start gap-2 p-2 rounded-lg bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 text-blue-700 dark:text-blue-300 text-xs">
                    <Info className="h-4 w-4 mt-0.5 shrink-0" />La date de résolution est automatiquement définie au passage en « Résolue ».
                  </div>
                )}
              </div>

              {/* Actions du tiroir */}
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={assignAnomaly}><UserCheck className="h-4 w-4" />Assigner</Button>
                <Button variant="outline" size="sm" onClick={() => flash('Modification (simulation)')}><Edit2 className="h-4 w-4" />Modifier</Button>
                <Button size="sm" onClick={resolveAnomaly} className="bg-gradient-to-r from-emerald-500 to-green-600 text-white"><CheckSquare className="h-4 w-4" />Résoudre</Button>
              </div>
              <p className="text-xs text-gray-400">Démo : « Assigner » attribue l'anomalie à <strong>k.diallo</strong> (statut → Assignée si Nouvelle) ; « Résoudre » la passe en <strong>Résolue</strong>.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ===== (3) Guide utilisateur ===== */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Guide utilisateur</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Plus className="h-5 w-5 text-blue-500" />Créer une anomalie</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Cliquer sur <strong>« Nouvelle Anomalie »</strong> (en haut à droite). <button onClick={createAnomaly} className="text-blue-600 dark:text-blue-400 underline">Essayer</button></GuideStep>
              <GuideStep n={2}>Renseigner le <strong>Titre</strong>, la <strong>Fonction</strong> et le <strong>Type</strong> (champs obligatoires).</GuideStep>
              <GuideStep n={3}>Choisir la <strong>Sévérité</strong> et l'<strong>Impact</strong>, puis décrire le problème.</GuideStep>
              <GuideStep n={4}>Le statut initial est <strong>Nouvelle</strong> et la date de détection est posée automatiquement.</GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><UserCheck className="h-5 w-5 text-blue-500" />Assigner une anomalie</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Sélectionner une anomalie dans le tableau pour ouvrir son détail.</GuideStep>
              <GuideStep n={2}>Cliquer sur <strong>« Assigner »</strong> et choisir un utilisateur. <button onClick={assignAnomaly} className="text-blue-600 dark:text-blue-400 underline">Essayer</button></GuideStep>
              <GuideStep n={3}>Le statut bascule de <strong>Nouvelle</strong> vers <strong>Assignée</strong>.</GuideStep>
              <GuideStep n={4}>Le responsable poursuit le traitement en passant l'anomalie <strong>En cours</strong>.</GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><CheckSquare className="h-5 w-5 text-blue-500" />Résoudre une anomalie</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Ouvrir le détail de l'anomalie traitée.</GuideStep>
              <GuideStep n={2}>Dans <strong>« Changer le statut »</strong>, choisir <strong>Résolue</strong>. <button onClick={resolveAnomaly} className="text-blue-600 dark:text-blue-400 underline">Essayer</button></GuideStep>
              <GuideStep n={3}>Renseigner la <strong>cause racine</strong> et la <strong>résolution</strong> ; la date de résolution est posée automatiquement.</GuideStep>
              <GuideStep n={4}>Après vérification, passer l'anomalie en <strong>Fermée</strong>.</GuideStep>
            </ol>
          </CardContent></Card>
        </div>
      </div>

      {/* ===== (4) Référence ===== */}
      {/* Types */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Code className="h-5 w-5 text-blue-500" />Types d'anomalie (AnomalyType)</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                <tr>{['Code', 'Libellé', 'Aperçu'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
                {ANOMALY_TYPES.map((t) => (
                  <tr key={t.value} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-2 font-mono text-xs">{t.value}</td>
                    <td className="px-4 py-2">{t.label}</td>
                    <td className="px-4 py-2"><TypeBadge type={t.value} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Statuts + Sévérités */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Tag className="h-5 w-5 text-blue-500" />Statuts (AnomalyStatus)</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                  <tr>{['Code', 'Libellé', 'Aperçu'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
                  {ANOMALY_STATUSES.map((s) => (
                    <tr key={s.value} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-2 font-mono text-xs">{s.value}</td>
                      <td className="px-4 py-2">{s.label}</td>
                      <td className="px-4 py-2"><StatusBadge status={s.value} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-blue-500" />Sévérités</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                  <tr>{['Code', 'Libellé', 'Aperçu'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
                  {ANOMALY_SEVERITIES.map((s) => (
                    <tr key={s.value} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-2 font-mono text-xs">{s.value}</td>
                      <td className="px-4 py-2">{s.label}</td>
                      <td className="px-4 py-2"><SeverityBadge severity={s.value} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Impacts */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><AlertOctagon className="h-5 w-5 text-blue-500" />Impacts métier</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                <tr>{['Code', 'Libellé', 'Aperçu'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
                {ANOMALY_IMPACTS.map((i) => (
                  <tr key={i.value} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-2 font-mono text-xs">{i.value}</td>
                    <td className="px-4 py-2">{i.label}</td>
                    <td className="px-4 py-2"><ImpactBadge impact={i.value} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Règles de gestion + Endpoints API */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Règles de gestion</h3>
          <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Titre, Fonction et Type sont obligatoires à la création.</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Cycle de vie : <strong>Nouvelle → Assignée → En cours → Résolue → Fermée</strong> (ou <strong>Rejetée</strong>).</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Le passage en <strong>Résolue</strong> renseigne automatiquement la date de résolution.</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Une anomalie est rattachée à une <strong>fonction</strong> ; le filtrage (statut/sévérité/type/fonction) s'effectue côté client.</li>
            <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />La suppression est <strong>irréversible</strong> ; privilégier le statut <strong>Rejetée</strong> ou <strong>Fermée</strong>.</li>
          </ul>
        </CardContent></Card>

        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Endpoints API</h3>
          <ul className="space-y-1.5 text-sm font-mono text-gray-700 dark:text-gray-200">
            {[
              ['GET', '#10B981', '/api/automata/anomalies'],
              ['POST', '#3B82F6', '/api/automata/anomalies'],
              ['PUT', '#F59E0B', '/api/automata/anomalies/{id}'],
              ['DELETE', '#EF4444', '/api/automata/anomalies/{id}'],
              ['PATCH', '#8B5CF6', '/api/automata/anomalies/{id}/assign?userId='],
              ['PATCH', '#8B5CF6', '/api/automata/anomalies/{id}/status?status='],
            ].map((r, i) => (
              <li key={i} className="flex items-center gap-2"><Badge color={r[1]}>{r[0]}</Badge><span className="break-all">{r[2]}</span></li>
            ))}
          </ul>
          <p className="text-xs text-gray-400 mt-3 flex items-center gap-1"><ArrowRight className="h-3 w-3" />Source : automata_environment / AnomalyController</p>
        </CardContent></Card>
      </div>
    </div>
  );
};

export default AnomaliesPOC;
